import {
  CASH_RECEIPT_FROM_CUSTOMERS,
  WAGES_AND_SALARIES,
  EXPENSES,
  EXTRA_INCOME,
  INVESTMENTS,
  DISTRIBUTIONS,
  INCOME_TAXES_PAID,
  ACTUAL_ENDING_CASH_BALANCE,
  BALANCE_CHANGES,
  DISCREPANCY,
  NET_CASH_FLOW,
  CASH_RECEIVED,
  CASH_GENERATED_BY_THE_PROJECT,
  EXCHANGE_OPERATIONS,
  ALL_CURRENCIES,
} from '../constants/constants';

import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';

import {
  getId,
  getValue,
  getValueByProject,
  getChildren,
  getChildrenByProject,
  getTempPeriod,
} from './treeCreationMethods';

/**
  * Description createTreeArr
  * Purpose of the function:
  * Create data tree
  * The function create an array of objects in which it accepts the component <TableTree/>
  * Object has name for left part table and value for right part table
  * Also Object can has children which content with the same objects
  * If object has children, this children is considered level 2, if object in children has own children is considered level 3
  * So component <TableTree/> render tree data
  *
  * @param {array} dateColumnsPeriod - Default 12 periods for columns 
  * @param {array} dataCashReceiptCustomers - Data from request
  * @param {array} dataCashReceiptCustomersChildren - Data from request
  * @param {array} dataSalaries - Data from request
  * @param {array} dataSalariesAdmin - Data from request
  * @param {array} dataSalariesAssigned - Data from request
  * @param {array} dataSalariesNotAssigned - Data from request
  * @param {array} dataExpenses - Data from request
  * @param {array} dataExpensesByCategory - Data from request
  * @param {array} dataExtraIncome - Data from request
  * @param {array} dataExtraIncomeByCategory - Data from request
  * @param {array} dataInvestments - Data from request
  * @param {array} dataInvestmentsByCategory - Data from request
  * @param {array} dataDistributions - Data from request
  * @param {array} dataDistributionsByCategory - Data from request
  * @param {array} dataTaxes - Data from request
  * @param {array} dataTaxesByCategory - Data from request
  * @param {array} dataBalanceChanges - Data from request
  * @param {array} dataDiscrepancy - Data from request
  * @param {array} dataExchangeOperations - Data from request
  * @param {array} dataNetCashFlow - Data from request
  * @param {string} currency - Selected currency, createTreeArr return array just data which has this currency
  * @param {string} renderBy - Show for what grouping use this function. Depending on this param 
                              function return array objects with different fields                       
  * @return {array} - returns an array of objects in which it accepts the component <TableTree/> 
 */

let fullPeriod = null;
let tempPeriod = [];
let period = null;

const createTreeArr = (
  dateColumnsPeriod,
  dataCashReceiptCustomers,
  dataCashReceiptCustomersChildren,
  dataSalaries,
  dataSalariesAdmin,
  dataSalariesAssigned,
  dataSalariesNotAssigned,
  dataExpenses,
  dataExpensesByCategory,
  dataExtraIncome,
  dataExtraIncomeByCategory,
  dataInvestments,
  dataInvestmentsByCategory,
  dataDistributions,
  dataDistributionsByCategory,
  dataTaxes,
  dataTaxesByCategory,
  dataBalances,
  dataBalanceChanges,
  dataDiscrepancy,
  dataExchangeOperations,
  dataNetCashFlow,
  currency,
  renderBy,
  selectedPeriod
) => {
  let treeArr = [];

  if (fullPeriod === null && dateColumnsPeriod.length === 12) fullPeriod = [...dateColumnsPeriod];
  if (selectedPeriod !== period && period) fullPeriod = [...dateColumnsPeriod];
  period = selectedPeriod;

  const createTree = () => {
    // Level 1
    // cashReceiptCustomers = Cash receipt from customers (name in table)
    if (dataCashReceiptCustomers) {
      const cashReceiptCustomers = {
        id: CASH_RECEIPT_FROM_CUSTOMERS + currency,
        name: CASH_RECEIPT_FROM_CUSTOMERS,
        value: getValue(fullPeriod || dateColumnsPeriod, dataCashReceiptCustomers, currency),
      };

      // Level 2
      // create and add children for cashReceiptCustomers
      if (dataCashReceiptCustomersChildren?.length > 0) {
        cashReceiptCustomers.children = getChildren(
          fullPeriod || dateColumnsPeriod,
          dataCashReceiptCustomersChildren,
          'project',
          currency
        );
      }

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, cashReceiptCustomers);
        cashReceiptCustomers.value = getValue(tempPeriod, dataCashReceiptCustomers, currency);
        cashReceiptCustomers.children = getChildren(tempPeriod, dataCashReceiptCustomersChildren, 'project', currency);
      }

      treeArr.push(cashReceiptCustomers);
    }

    // Level 2
    // administrativePersonnel = Administrative personnel (name in table)
    const administrativePersonnel = {
      id: getId(),
      name: 'Administrative personnel',
      value: getValue(fullPeriod || dateColumnsPeriod, dataSalariesAdmin, currency),
    };

    // Take values from tempPeriod until the dateColumnsPeriod array is ready
    // This is for progressively rendering of columns
    if (fullPeriod) {
      tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, administrativePersonnel);
      administrativePersonnel.value = getValue(tempPeriod, dataSalariesAdmin, currency);
    }

    // Level 2
    // employeesAssignedProjects = Employees assigned to the projects (name in table)
    const employeesAssignedProjects = {
      id: getId(),
      name: 'Employees assigned to the projects',
      value: getValue(fullPeriod || dateColumnsPeriod, dataSalariesAssigned, currency),
    };

    // Take values from tempPeriod until the dateColumnsPeriod array is ready
    // This is for progressively rendering of columns
    if (fullPeriod) {
      tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, employeesAssignedProjects);
      employeesAssignedProjects.value = getValue(tempPeriod, dataSalariesAssigned, currency);
    }

    // Level 2
    // employeesNotAssignedProjects = Employees on bench (name in table)
    const employeesNotAssignedProjects = {
      id: getId(),
      name: 'Employees on bench',
      value: getValue(fullPeriod || dateColumnsPeriod, dataSalariesNotAssigned, currency),
    };

    // Take values from tempPeriod until the dateColumnsPeriod array is ready
    // This is for progressively rendering of columns
    if (fullPeriod) {
      tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, employeesNotAssignedProjects);
      employeesNotAssignedProjects.value = getValue(tempPeriod, dataSalariesNotAssigned, currency);
    }

    // Level 1
    // extraIncome = Extra Income (name in table)
    if (dataExtraIncome) {
      const extraIncome = {
        id: EXTRA_INCOME + currency,
        name: EXTRA_INCOME,
        value: getValue(fullPeriod || dateColumnsPeriod, dataExtraIncome, currency),
      };

      // Level 2
      // create and add children for extraIncome
      if (dataExtraIncomeByCategory?.length > 0) {
        extraIncome.children = getChildren(
          fullPeriod || dateColumnsPeriod,
          dataExtraIncomeByCategory,
          'title',
          currency
        );
      }

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, extraIncome);
        extraIncome.value = getValue(tempPeriod, dataExtraIncome, currency);
        extraIncome.children = getChildren(tempPeriod, dataExtraIncomeByCategory, 'title', currency);
      }
      treeArr.push(extraIncome);
    }

    // Level 1
    // wagesSalaries = Wages and salaries (name in table)
    if (dataSalaries) {
      const wagesSalaries = {
        id: WAGES_AND_SALARIES + currency,
        name: WAGES_AND_SALARIES,
        value: getValue(fullPeriod || dateColumnsPeriod, dataSalaries, currency),
        children: [administrativePersonnel, employeesAssignedProjects, employeesNotAssignedProjects],
      };

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, wagesSalaries);
        wagesSalaries.value = getValue(tempPeriod, dataSalaries, currency);
      }
      treeArr.push(wagesSalaries);
    }

    // Level 1
    // expenses = Expenses (name in table)
    if (dataExpenses) {
      const expenses = {
        id: EXPENSES + currency,
        name: EXPENSES,
        value: getValue(fullPeriod || dateColumnsPeriod, dataExpenses, currency),
      };

      // Level 2
      // create and add children for expenses
      if (dataExpensesByCategory?.length > 0) {
        expenses.children = getChildren(fullPeriod || dateColumnsPeriod, dataExpensesByCategory, 'title', currency);
      }

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, expenses);
        expenses.value = getValue(tempPeriod, dataExpenses, currency);
        expenses.children = getChildren(tempPeriod, dataExpensesByCategory, 'title', currency);
      }
      treeArr.push(expenses);
    }

    // Level 1
    // investments = Investments (name in table)
    if (dataInvestments) {
      const investments = {
        id: INVESTMENTS + currency,
        name: INVESTMENTS,
        value: getValue(fullPeriod || dateColumnsPeriod, dataInvestments, currency),
      };

      // create and add children for investments
      if (dataInvestmentsByCategory?.length > 0) {
        investments.children = getChildren(
          fullPeriod || dateColumnsPeriod,
          dataInvestmentsByCategory,
          'title',
          currency
        );
      }

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, investments);
        investments.value = getValue(tempPeriod, dataInvestments, currency);
        investments.children = getChildren(tempPeriod, dataInvestmentsByCategory, 'title', currency);
      }
      treeArr.push(investments);
    }

    // Level 1
    // distributions = Distributions (name in table)
    if (dataDistributions) {
      const distributions = {
        id: DISTRIBUTIONS + currency,
        name: DISTRIBUTIONS,
        value: getValue(fullPeriod || dateColumnsPeriod, dataDistributions, currency),
      };

      // Level 2
      // create and add children for distributions
      if (dataDistributionsByCategory?.length > 0) {
        distributions.children = getChildren(
          fullPeriod || dateColumnsPeriod,
          dataDistributionsByCategory,
          'title',
          currency
        );
      }

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, distributions);
        distributions.value = getValue(tempPeriod, dataDistributions, currency);
        distributions.children = getChildren(tempPeriod, dataDistributionsByCategory, 'title', currency);
      }
      treeArr.push(distributions);
    }

    // Level 1
    // taxes = Income taxes paid (name in table)
    if (dataTaxes) {
      const taxes = {
        id: INCOME_TAXES_PAID + currency,
        name: INCOME_TAXES_PAID,
        value: getValue(fullPeriod || dateColumnsPeriod, dataTaxes, currency),
      };

      // Level 2
      // create and add children for taxes
      if (dataTaxesByCategory?.length > 0) {
        taxes.children = getChildren(fullPeriod || dateColumnsPeriod, dataTaxesByCategory, 'title', currency);
      }

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, taxes);
        taxes.value = getValue(tempPeriod, dataTaxes, currency);
        taxes.children = getChildren(tempPeriod, dataTaxesByCategory, 'title', currency);
      }

      treeArr.push(taxes);
    }

    if (renderBy === ALL_CURRENCIES && dataExchangeOperations) {
      // Level 1
      // exchangeOperations = Exchange operations (name in table)
      const exchangeOperations = {
        id: EXCHANGE_OPERATIONS + currency,
        name: EXCHANGE_OPERATIONS,
        value: getValue(fullPeriod || dateColumnsPeriod, dataExchangeOperations, currency),
      };

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, exchangeOperations);
        exchangeOperations.value = getValue(tempPeriod, dataExchangeOperations, currency);
      }
      treeArr.push(exchangeOperations);
    }

    // Level 1
    // balances = Actual ending cash balance (name in table)
    if (dataBalances) {
      const balances = {
        id: ACTUAL_ENDING_CASH_BALANCE + currency,
        name: ACTUAL_ENDING_CASH_BALANCE,
        value: getValue(fullPeriod || dateColumnsPeriod, dataBalances, currency),
      };

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, balances);
        balances.value = getValue(tempPeriod, dataBalances, currency);
      }
      treeArr.push(balances);
    }

    // Level 1
    // balanceChanges = Balance changes (name in table)
    if (dataBalanceChanges) {
      const balanceChanges = {
        id: BALANCE_CHANGES + currency,
        name: BALANCE_CHANGES,
        value: getValue(fullPeriod || dateColumnsPeriod, dataBalanceChanges, currency),
      };

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, balanceChanges);
        balanceChanges.value = getValue(tempPeriod, dataBalanceChanges, currency);
      }
      treeArr.push(balanceChanges);
    }

    // Level 1
    // netCashFlow = Net cash flow (name in table)
    if (dataNetCashFlow) {
      const netCashFlow = {
        id: NET_CASH_FLOW + currency,
        name: NET_CASH_FLOW,
        value: getValue(fullPeriod || dateColumnsPeriod, dataNetCashFlow, currency),
      };

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, netCashFlow);
        netCashFlow.value = getValue(tempPeriod, dataNetCashFlow, currency);
      }
      treeArr.push(netCashFlow);
    }

    // Level 1
    // discrepancy = Discrepancy (name in table)
    if (dataDiscrepancy) {
      const discrepancy = {
        id: DISCREPANCY + currency,
        name: DISCREPANCY,
        value: getValue(fullPeriod || dateColumnsPeriod, dataDiscrepancy, currency),
      };

      // Take values from tempPeriod until the dateColumnsPeriod array is ready
      // This is for progressively rendering of columns
      if (fullPeriod) {
        tempPeriod = getTempPeriod(tempPeriod, fullPeriod, dateColumnsPeriod, discrepancy);
        discrepancy.value = getValue(tempPeriod, dataDiscrepancy, currency);
      }
      treeArr.push(discrepancy);
    }
  };

  if (renderBy !== ALL_CURRENCIES) {
    createTree();
  }

  if (
    dataCashReceiptCustomers &&
    dataCashReceiptCustomersChildren &&
    dataSalaries &&
    dataSalariesAdmin &&
    dataSalariesAssigned &&
    dataSalariesNotAssigned &&
    dataExpenses &&
    dataExpensesByCategory &&
    dataExtraIncome &&
    dataExtraIncomeByCategory &&
    dataInvestments &&
    dataInvestmentsByCategory &&
    dataDistributions &&
    dataDistributionsByCategory &&
    dataTaxes &&
    dataTaxesByCategory &&
    dataBalances &&
    dataBalanceChanges &&
    dataDiscrepancy &&
    dataNetCashFlow &&
    fullPeriod
  ) {
    treeArr = [];
    createTree();
    fullPeriod = null;
    if (renderBy !== ALL_CURRENCIES) {
      tempPeriod = [];
    }
  }

  return treeArr;
};

let fullPeriodByProject = null;
let tempPeriodByProject = [];
let periodByProject = null;

const createTreeArrProject = (
  dateColumnsPeriod,
  dataNetCashFlowByProject,
  dataPaymentsByPrj,
  dataSalariesByProject,
  dataSalariesByEmployee,
  dataExpensesProject,
  dataExpensesBPC,
  selectedPeriod
) => {
  let filterProjectByName = [];
  let treeArrByProject = [];

  if (fullPeriodByProject === null && dateColumnsPeriod.length === 12) fullPeriodByProject = [...dateColumnsPeriod];
  if (selectedPeriod !== periodByProject && periodByProject) fullPeriodByProject = [...dateColumnsPeriod];
  periodByProject = selectedPeriod;

  const createTree = () => {
    // Build tree if query returned dataExpensesProject
    if (dataExpensesProject) {
      filterProjectByName = uniqBy(dataExpensesProject, 'project');

      let tempArr = [];

      //Level 1
      //Projects
      tempArr = filterProjectByName.map((item) => {
        const project = {
          id: item.project,
          name: item.project,
          value: getValueByProject(item.project, fullPeriodByProject || dateColumnsPeriod, dataExpensesProject),
        };

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(tempPeriodByProject, fullPeriodByProject, dateColumnsPeriod, project);
          project.value = getValueByProject(item.project, tempPeriodByProject, dataExpensesProject);
        }

        return project;
      });

      tempArr = uniqBy([...treeArrByProject, ...tempArr], 'name');
      treeArrByProject = tempArr.map((item) => {
        let newObj = { ...item, children: item.children || [] };

        //Level 2
        // expensesByProject = Expenses (name in table)
        if (dataExpensesProject) {
          const expensesByProject = {
            id: EXPENSES + item.name,
            name: EXPENSES,
            value: getValueByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataExpensesProject),
          };

          // create and add children for expensesByProject
          if (dataExpensesBPC?.length > 0) {
            getChildrenByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataExpensesBPC, 'title').length >
              0 &&
              (expensesByProject.children = getChildrenByProject(
                item.name,
                fullPeriodByProject || dateColumnsPeriod,
                dataExpensesBPC,
                'title'
              ));
          }

          if (fullPeriodByProject) {
            tempPeriodByProject = getTempPeriod(
              tempPeriodByProject,
              fullPeriodByProject,
              dateColumnsPeriod,
              expensesByProject
            );
            expensesByProject.value = getValueByProject(item.project, tempPeriodByProject, dataExpensesProject);
            expensesByProject.children = getChildrenByProject(item.name, tempPeriodByProject, dataExpensesBPC, 'title');
          }

          newObj.children.push(expensesByProject);
        }

        return newObj;
      });
    }

    //Rebuild tree if query returned dataPaymentsByPrj
    if (dataPaymentsByPrj) {
      let tempArr = [];
      filterProjectByName = uniqBy(dataPaymentsByPrj, 'project');

      //Level 1
      //Projects
      tempArr = filterProjectByName.map((item) => {
        const project = {
          id: item.project,
          name: item.project,
          value: getValueByProject(item.project, fullPeriodByProject || dateColumnsPeriod, dataPaymentsByPrj),
        };

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(tempPeriodByProject, fullPeriodByProject, dateColumnsPeriod, project);
          project.value = getValueByProject(item.project, tempPeriodByProject, dataPaymentsByPrj);
        }

        return project;
      });

      tempArr = uniqBy([...treeArrByProject, ...tempArr], 'name');
      treeArrByProject = tempArr.map((item) => {
        let newObj = { ...item, children: item.children || [] };
        //Level 2
        //cashReceived = Cash received (name in table)
        const cashReceived = {
          id: CASH_RECEIVED + item.name,
          name: CASH_RECEIVED,
          value: getValueByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataPaymentsByPrj),
        };

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(
            tempPeriodByProject,
            fullPeriodByProject,
            dateColumnsPeriod,
            cashReceived
          );
          cashReceived.value = getValueByProject(item.project, tempPeriodByProject, dataPaymentsByPrj);
        }

        newObj.children.push(cashReceived);
        return newObj;
      });
    }

    // Rebuild tree if query returned dataSalariesByProject
    if (dataSalariesByProject) {
      let tempArr = [];
      filterProjectByName = uniqBy(dataSalariesByProject, 'project');

      //Level 1
      //Projects
      tempArr = filterProjectByName.map((item) => {
        const project = {
          id: item.project,
          name: item.project,
          value: getValueByProject(item.project, fullPeriodByProject || dateColumnsPeriod, dataSalariesByProject),
        };

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(tempPeriodByProject, fullPeriodByProject, dateColumnsPeriod, project);
          project.value = getValueByProject(item.project, tempPeriodByProject, dataSalariesByProject);
        }

        return project;
      });

      tempArr = uniqBy([...treeArrByProject, ...tempArr], 'name');
      treeArrByProject = tempArr.map((item) => {
        let newObj = { ...item, children: item.children || [] };

        //Level 2
        // wagesSalaries = Wages and salaries (name in table)
        const wagesSalaries = {
          id: WAGES_AND_SALARIES + item.name,
          name: WAGES_AND_SALARIES,
          value: getValueByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataSalariesByProject),
        };

        // create and add children for wagesSalaries
        if (dataSalariesByEmployee?.length > 0) {
          getChildrenByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataSalariesByEmployee, 'title')
            .length > 0 &&
            (wagesSalaries.children = getChildrenByProject(
              item.name,
              fullPeriodByProject || dateColumnsPeriod,
              dataSalariesByEmployee,
              'title'
            ));
        }

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(
            tempPeriodByProject,
            fullPeriodByProject,
            dateColumnsPeriod,
            wagesSalaries
          );
          wagesSalaries.value = getValueByProject(item.name, tempPeriodByProject, dataSalariesByProject);
          wagesSalaries.children = getChildrenByProject(
            item.name,
            tempPeriodByProject,
            dataSalariesByEmployee,
            'title'
          );
        }

        newObj.children.push(wagesSalaries);
        return newObj;
      });
    }
    // Rebuild tree if queries returned all data
    if (dataNetCashFlowByProject) {
      let tempArr = [];
      filterProjectByName = uniqBy(dataNetCashFlowByProject, 'project');

      //Level 1
      //Projects
      tempArr = filterProjectByName.map((item) => {
        const project = {
          id: item.project,
          name: item.project,
          value: getValueByProject(item.project, fullPeriodByProject || dateColumnsPeriod, dataNetCashFlowByProject),
        };

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(tempPeriodByProject, fullPeriodByProject, dateColumnsPeriod, project);
          project.value = getValueByProject(item.project, tempPeriodByProject, dataNetCashFlowByProject);
        }

        return project;
      });

      // Move children from treeArrByProject to tempArr
      tempArr = tempArr.map((item) => {
        treeArrByProject.forEach((item2) => {
          if (item.name === item2.name) item.children = [...item2.children];
        });
        return item;
      });

      //Projects
      treeArrByProject = tempArr.map((item, i) => {
        let newObj = { ...item, children: item.children || [] };
        //Level 2
        //cashGeneratedByProject = Cash generated by the project(name in table)
        const cashGeneratedByProject = {
          id: CASH_GENERATED_BY_THE_PROJECT + item.name,
          name: CASH_GENERATED_BY_THE_PROJECT,
          value: getValueByProject(item.name, fullPeriodByProject || dateColumnsPeriod, dataNetCashFlowByProject),
        };

        // Take values from tempPeriodByProject until the dateColumnsPeriod array is ready
        // This is for progressively rendering of columns
        if (fullPeriodByProject) {
          tempPeriodByProject = getTempPeriod(
            tempPeriodByProject,
            fullPeriodByProject,
            dateColumnsPeriod,
            cashGeneratedByProject
          );
          cashGeneratedByProject.value = getValueByProject(item.project, tempPeriodByProject, dataNetCashFlowByProject);
        }

        newObj.children.push(cashGeneratedByProject);
        return newObj;
      });
    }
    // Sorting children in a correct order
    treeArrByProject = treeArrByProject.map((item) => {
      let newChildrenOrder = [];
      item.children.forEach((child) => {
        if (child?.name === 'Cash received' && child.value) newChildrenOrder[0] = child;
        if (child?.name === 'Wages and salaries' && child.value) newChildrenOrder[1] = child;
        if (child?.name === 'Expenses' && child.value) newChildrenOrder[2] = child;
        if (child?.name === 'Cash generated by the project' && child.value) newChildrenOrder[3] = child;
      });
      item.children = [...newChildrenOrder].filter((item) => item !== undefined);
      return item;
    });
  };

  createTree();

  if (
    dataNetCashFlowByProject &&
    dataPaymentsByPrj &&
    dataSalariesByProject &&
    dataSalariesByEmployee &&
    dataExpensesProject &&
    dataExpensesBPC &&
    fullPeriodByProject
  ) {
    treeArrByProject = [];
    createTree();
    fullPeriodByProject = null;
    tempPeriodByProject = [];
  }

  return sortBy(treeArrByProject, 'name');
};

let fullPeriodAllCurrency = null;

const createTreeArrCurrency = (
  dateColumnsPeriod,
  dataCashReceiptCustomers,
  dataCashReceiptCustomersChildren,
  dataSalaries,
  dataSalariesAdmin,
  dataSalariesAssigned,
  dataSalariesNotAssigned,
  dataExpenses,
  dataExpensesByCategory,
  dataExtraIncome,
  dataExtraIncomeByCategory,
  dataInvestments,
  dataInvestmentsByCategory,
  dataDistributions,
  dataDistributionsByCategory,
  dataTaxes,
  dataTaxesByCategory,
  dataBalances,
  dataBalanceChanges,
  dataDiscrepancy,
  dataExchangeOperations,
  dataNetCashFlow,
  selectedPeriod
) => {
  let allCurrencies = [];
  let treeArrAllCurrency = [];

  if (fullPeriodAllCurrency === null && dateColumnsPeriod.length === 12) fullPeriodAllCurrency = [...dateColumnsPeriod];

  const createTree = () => {
    const getMergedCurrencies = (prevCurrencies, data) => {
      return [...new Set([...prevCurrencies, ...data.map((item) => item.currency)])];
    };

    if (dataCashReceiptCustomers) allCurrencies = getMergedCurrencies(allCurrencies, dataCashReceiptCustomers);
    if (dataCashReceiptCustomersChildren)
      allCurrencies = getMergedCurrencies(allCurrencies, dataCashReceiptCustomersChildren);
    if (dataSalaries) allCurrencies = getMergedCurrencies(allCurrencies, dataSalaries);
    if (dataSalariesAdmin) allCurrencies = getMergedCurrencies(allCurrencies, dataSalariesAdmin);
    if (dataSalariesNotAssigned) allCurrencies = getMergedCurrencies(allCurrencies, dataSalariesNotAssigned);
    if (dataSalariesAssigned) allCurrencies = getMergedCurrencies(allCurrencies, dataSalariesAssigned);
    if (dataExpenses) allCurrencies = getMergedCurrencies(allCurrencies, dataExpenses);
    if (dataExpensesByCategory) allCurrencies = getMergedCurrencies(allCurrencies, dataExpensesByCategory);
    if (dataExtraIncome) allCurrencies = getMergedCurrencies(allCurrencies, dataExtraIncome);
    if (dataExtraIncomeByCategory) allCurrencies = getMergedCurrencies(allCurrencies, dataExtraIncomeByCategory);
    if (dataInvestments) allCurrencies = getMergedCurrencies(allCurrencies, dataInvestments);
    if (dataInvestmentsByCategory) allCurrencies = getMergedCurrencies(allCurrencies, dataInvestmentsByCategory);
    if (dataDistributions) allCurrencies = getMergedCurrencies(allCurrencies, dataDistributions);
    if (dataDistributionsByCategory) allCurrencies = getMergedCurrencies(allCurrencies, dataDistributionsByCategory);
    if (dataTaxes) allCurrencies = getMergedCurrencies(allCurrencies, dataTaxes);
    if (dataTaxesByCategory) allCurrencies = getMergedCurrencies(allCurrencies, dataTaxesByCategory);
    if (dataBalances) allCurrencies = getMergedCurrencies(allCurrencies, dataBalances);
    if (dataBalanceChanges) allCurrencies = getMergedCurrencies(allCurrencies, dataBalanceChanges);
    if (dataDiscrepancy) allCurrencies = getMergedCurrencies(allCurrencies, dataDiscrepancy);
    if (dataExchangeOperations) allCurrencies = getMergedCurrencies(allCurrencies, dataExchangeOperations);
    if (dataNetCashFlow) allCurrencies = getMergedCurrencies(allCurrencies, dataNetCashFlow);

    if (allCurrencies.length > 0) {
      treeArrAllCurrency = allCurrencies.map((itemCurrency) => {
        const currency = {
          id: itemCurrency,
          name: itemCurrency,
          // value: Add value after created children from 'Net cash flow'
        };

        currency.children = createTreeArr(
          dateColumnsPeriod,
          dataCashReceiptCustomers || [],
          dataCashReceiptCustomersChildren || [],
          dataSalaries || [],
          dataSalariesAdmin || [],
          dataSalariesAssigned || [],
          dataSalariesNotAssigned || [],
          dataExpenses || [],
          dataExpensesByCategory || [],
          dataExtraIncome || [],
          dataExtraIncomeByCategory || [],
          dataInvestments || [],
          dataInvestmentsByCategory || [],
          dataDistributions || [],
          dataDistributionsByCategory || [],
          dataTaxes || [],
          dataTaxesByCategory || [],
          dataBalances || [],
          dataBalanceChanges || [],
          dataDiscrepancy || [],
          dataExchangeOperations || [],
          dataNetCashFlow || [],
          itemCurrency,
          ALL_CURRENCIES,
          selectedPeriod
        );

        // Add value from 'Net cash flow'
        currency.children.forEach((item) => {
          if (item.name === 'Net cash flow') {
            currency.value = item.value;
          }
        });
        return currency;
      });
    }
  };

  createTree();

  if (
    dataCashReceiptCustomers &&
    dataCashReceiptCustomersChildren &&
    dataSalaries &&
    dataSalariesAdmin &&
    dataSalariesAssigned &&
    dataSalariesNotAssigned &&
    dataExpenses &&
    dataExpensesByCategory &&
    dataExtraIncome &&
    dataExtraIncomeByCategory &&
    dataInvestments &&
    dataInvestmentsByCategory &&
    dataDistributions &&
    dataDistributionsByCategory &&
    dataTaxes &&
    dataTaxesByCategory &&
    dataBalances &&
    dataBalanceChanges &&
    dataDiscrepancy &&
    dataExchangeOperations &&
    dataNetCashFlow
  ) {
    createTree();
  }

  return treeArrAllCurrency;
};

export { createTreeArr, createTreeArrProject, createTreeArrCurrency };
